import { Check } from 'lucide-react';

type JourneyStep = {
  id: string;
  label: string;
  description?: string;
  duration?: string;
};

const careSeekerSteps: JourneyStep[] = [
  { id: 'intake', label: 'Your Story', description: 'Tell us about your situation', duration: '5-10 min' },
  { id: 'summary', label: 'Your Journey', description: 'Understanding what to expect', duration: '3-5 min' },
  { id: 'overview', label: 'Provider Types', description: 'Learn about care options', duration: '5-7 min' },
  { id: 'matching', label: 'Find Providers', description: 'Match with specialists', duration: '10-15 min' },
  { id: 'plan', label: 'Care Plan', description: 'Your personalized roadmap', duration: '5-10 min' },
];

// FP-skinned: the Q&A collapses to two steps, and the chatbot's intermediate
// exploration screens are skipped (handoff jumps from `intake` straight to
// the FP results page).
const fpSteps: JourneyStep[] = [
  { id: 'intake', label: 'Your Story', description: 'Tell us about your client' },
  { id: 'matches', label: 'Your Matches', description: 'Recommended specialists & resources' },
];

export type JourneyProgressMode = 'care-seeker' | 'fp';

type JourneyProgressProps = {
  currentStep: string;
  mode?: JourneyProgressMode;
};

export function JourneyProgress({ currentStep, mode = 'care-seeker' }: JourneyProgressProps) {
  const steps = mode === 'fp' ? fpSteps : careSeekerSteps;
  const currentIndex = Math.max(
    0,
    steps.findIndex((step) => step.id === currentStep),
  );
  const safeCurrent = steps[currentIndex] ?? steps[0];
  const trackInset = 100 / (steps.length * 2);
  const progressWidth =
    steps.length > 1 ? (currentIndex / (steps.length - 1)) * (100 - trackInset * 2) : 0;

  return (
    <div className="sticky top-[var(--app-topbar-h,4rem)] z-40 border-b border-gray-200 bg-background px-4 py-4 shadow-sm">
      <div className="mx-auto max-w-5xl">
        <div className="group relative">
          <div
            className="absolute top-5 h-0.5 bg-gray-200"
            style={{ left: `${trackInset}%`, right: `${trackInset}%` }}
            aria-hidden
          />
          <div
            className="absolute top-5 h-0.5 bg-[#026857] transition-all"
            style={{ left: `${trackInset}%`, width: `${progressWidth}%` }}
            aria-hidden
          />
          <div
            className="relative z-[1] grid"
            style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
          >
            {steps.map((step, index) => {
              const isCompleted = index < currentIndex;
              const isCurrent = index === currentIndex;

              return (
                <div key={step.id} className="flex min-w-0 flex-col items-center gap-2 px-2">
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition-all ${
                        isCompleted
                          ? 'bg-[#026857] text-white'
                          : isCurrent
                            ? 'bg-[#026857] text-white ring-4 ring-[#026857]/20'
                            : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {isCompleted ? <Check size={20} /> : <span>{index + 1}</span>}
                    </div>

                    <div className="hidden text-center sm:block">
                      <div
                        className={`text-sm font-semibold ${
                          isCurrent ? 'text-[#026857]' : isCompleted ? 'text-gray-900' : 'text-gray-500'
                        }`}
                      >
                        {step.label}
                      </div>
                      <div className="max-h-0 overflow-hidden transition-all duration-300 group-hover:max-h-10">
                        <div className="mt-1 max-w-[100px] text-xs text-gray-500">
                          {step.description}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-4 text-center sm:hidden">
          <div className="text-sm font-semibold text-[#026857]">{safeCurrent.label}</div>
          {safeCurrent.description && (
            <div className="mt-1 text-xs text-gray-500">{safeCurrent.description}</div>
          )}
          {safeCurrent.duration && (
            <div className="mt-1 text-xs text-gray-500">{safeCurrent.duration}</div>
          )}
        </div>
      </div>
    </div>
  );
}
